import { useCallback, useEffect, useState } from 'react'
import { config, type ThemeChoice } from '@/config/runtime-config'

const KEY = 'axis.theme'

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark') return v
  } catch {
    /* private mode */
  }
  return config.defaultTheme
}

/**
 * Theme follows the OS on first run; once the user picks explicitly, that wins
 * and persists. "Match system" clears the stored value.
 *
 * ## It is the `.dark` CLASS that does the work, not `data-theme`
 *
 * This hook used to set `data-theme="dark"` and nothing else. Nothing reads that:
 * the stylesheet declares `@custom-variant dark (&:is(.dark *))` and its whole token
 * block is `.dark { … }`, and every map component asks
 * `document.documentElement.classList.contains('dark')`. So the attribute was set,
 * no rule matched it, and the app stayed light whatever anyone chose — a theme
 * system that was fully written and entirely disconnected.
 *
 * The class is therefore the mechanism. `data-theme` is kept alongside it as a
 * mirror, because it is what a debugger, a screenshot script or a future
 * `[data-theme]` rule would look for, and because removing it would break nothing
 * visibly today and confuse somebody later.
 *
 * `color-scheme` is set too: it is what makes the browser's own furniture — form
 * controls, scrollbars, the flash behind a page load — follow the theme. Without
 * it a dark console still renders white scrollbars.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(read)
  const [resolved, setResolved] = useState<'light' | 'dark'>(
    () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light'),
  )

  useEffect(() => {
    const apply = () => {
      const next = choice === 'system' ? systemTheme() : choice
      const root = document.documentElement
      // The maps watch this class with a MutationObserver, so toggle it exactly
      // once per change rather than removing and re-adding it.
      root.classList.toggle('dark', next === 'dark')
      root.setAttribute('data-theme', next)
      root.style.colorScheme = next
      setResolved(next)
    }
    apply()
    if (choice !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [choice])

  const set = useCallback((next: ThemeChoice) => {
    try {
      if (next === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      /* private mode */
    }
    setChoice(next)
  }, [])

  return { choice, resolved, setTheme: set }
}
